/**
 * Notifications - 消息通知 Tab
 */
import { useState, useEffect } from 'react';
import { Bell, Gift, CheckCircle, Info } from 'lucide-react';

interface Notification {
  id: string;
  type: 'bonus' | 'system' | 'success';
  title: string;
  content: string;
  isRead: boolean;
  createdAt: string;
}

export default function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 模拟数据
    setTimeout(() => {
      setNotifications([
        {
          id: '1',
          type: 'bonus',
          title: '体验金到账',
          content: '欢迎注册 BadiBook！赠送 ¥10 体验金已到账，可用于创作绘本。',
          isRead: false,
          createdAt: '2024-01-15T10:00:00',
        },
        {
          id: '2',
          type: 'success',
          title: '充值成功',
          content: '您的充值 ¥99 已成功到账，赠送 ¥15 已发放。',
          isRead: true,
          createdAt: '2024-01-10T14:30:00',
        },
        {
          id: '3',
          type: 'system',
          title: '新功能上线',
          content: 'AI 绘本创作功能全新升级，支持更多风格选择！',
          isRead: true,
          createdAt: '2024-01-05T09:00:00',
        },
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
    );
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'bonus':
        return <Gift className="w-5 h-5 text-amber-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getBgColor = (type: string) => {
    switch (type) {
      case 'bonus':
        return 'bg-amber-50';
      case 'success':
        return 'bg-green-50';
      default:
        return 'bg-blue-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="text-center py-20">
        <Bell className="w-16 h-16 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">暂无消息通知</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          onClick={() => markAsRead(notification.id)}
          className={`relative rounded-xl border transition-all cursor-pointer p-4 ${
            notification.isRead
              ? 'bg-white border-slate-200'
              : 'bg-indigo-50/50 border-indigo-200'
          }`}
        >
          {/* 未读标记 */}
          {!notification.isRead && (
            <div className="absolute top-4 right-4 w-2 h-2 bg-red-500 rounded-full" />
          )}

          <div className="flex gap-4">
            <div className={`w-10 h-10 rounded-xl ${getBgColor(notification.type)} flex items-center justify-center flex-shrink-0`}>
              {getIcon(notification.type)}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-medium text-slate-800">{notification.title}</h4>
                {!notification.isRead && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
                    未读
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 mb-2">{notification.content}</p>
              <p className="text-xs text-slate-400">
                {new Date(notification.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
